'use client'

import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useShoppingCart } from '@/ShoppingCart/useShoppingCart'
import { useUpdateShoppingCartLocationMutation } from '@/generated/graphql'
import { isEuropeanCountry } from '../../../lib/middleware/variantRedirect.helper'
import ReactFlagsSelect from 'react-flags-select'
import { Button } from '@/General/Button'
import { toastEmitter } from '@/General/Toast'
import { ArrowPathIcon } from '@heroicons/react/24/solid'
import { useRouter } from 'next/navigation'
import { useSearchParams } from 'next/navigation'
import { useCookie } from '@/Cookies/useCookies'
import { Features } from './Features'
import {
  navigateToMarketForBrand,
  sanitizeRedirectPath,
} from '@/lib/auth/domainSwitch'
import { BRAND_COOKIE, COUNTRY_COOKIE } from '@/lib/auth/marketCookies'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'
import { useStore } from '@/store'
import { useStorefrontMarket } from '@/useStorefrontMarket'

export const CurrencySelector = ({ countries }: CountryClientProps) => {
  const countryLabels = useMemo(
    () =>
      Object.fromEntries(
        countries.map(c => [
          c.isoCode,
          { primary: c.name, secondary: '(currency: ' + c.currencyCode + ')' },
        ])
      ),
    [countries]
  )

  const { setCookie } = useCookie()
  const setCountry = useStore(state => state.setCountry)
  const shoppingCartId = useStore(state => state.shoppingCartId)
  const searchParams = useSearchParams()
  const ref = searchParams.get('ref')
  const router = useRouter()
  const { cartData, isLoading: isCartLoading } = useShoppingCart()
  const storefrontMarket = useStorefrontMarket({
    cartCountryIsoCode: cartData?.country?.isoCode,
  })
  const [selectedCountryOverride, setSelectedCountryOverride] = useState<
    string | null
  >(null)
  const queryClient = useQueryClient()
  const selectedCountry =
    selectedCountryOverride ?? storefrontMarket.countryIsoCode

  const navigateToSelectedMarket = async (
    brandCode: RegionalBrandCode,
    countryIsoCode: string
  ) => {
    const safeRedirect = sanitizeRedirectPath(ref, '/')
    await navigateToMarketForBrand({
      brandCode,
      redirectPath: ref ?? '/',
      selectedCountryIsoCode: countryIsoCode,
      shoppingCartToken: cartData?.token ?? shoppingCartId,
      onSameHost: () => {
        setCookie(BRAND_COOKIE, brandCode)
        setCookie(COUNTRY_COOKIE, countryIsoCode)
        setCountry(countryIsoCode)
        setSelectedCountryOverride(null)
        router.push(safeRedirect)
      },
    })
  }

  const getSelectedBrandCode = (): RegionalBrandCode | null => {
    const brandCode = countries.find(
      country => country.isoCode === selectedCountry
    )?.brandCode

    if (brandCode === 'VANISHING_INC_UK') return 'VANISHING_INC_UK'
    if (brandCode === 'VANISHING_INC_US') return 'VANISHING_INC_US'
    return null
  }

  const { mutate: updateShoppingCartLocation, isPending } =
    useUpdateShoppingCartLocationMutation({
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: ['ActiveShoppingCart'],
        })

        const brandCode = getSelectedBrandCode()
        if (!brandCode) return

        toastEmitter.emit('toast', {
          title: 'Updated',
          body: `Prices in ${isEuropeanCountry(selectedCountry || '') ? 'GBP' : 'USD ($)'}`,
          variant: 'success',
          duration: 3000,
        })

        await navigateToSelectedMarket(brandCode, selectedCountry)
      },
    })

  const updateCountryAndCurrency = async () => {
    if (cartData?.token && selectedCountry) {
      updateShoppingCartLocation({
        shoppingCartToken: cartData.token,
        countryIsoCode: selectedCountry,
      })
      return
    }

    if (!selectedCountry) return

    const newBrandCode = getSelectedBrandCode()
    if (!newBrandCode) return

    toastEmitter.emit('toast', {
      title: 'Updated',
      body: `Prices in ${isEuropeanCountry(selectedCountry || '') ? 'GBP' : 'USD ($)'}`,
      variant: 'success',
      duration: 3000,
    })

    await navigateToSelectedMarket(newBrandCode, selectedCountry)
  }

  return (
    <div key={cartData?.country?.isoCode}>
      <section className="rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm ring-1 ring-black/[0.04] sm:p-6">
        {selectedCountry && (
          <div className="space-y-6">
            <div>
              <div className="mb-2 text-base font-semibold text-gray-900">
                Country
              </div>
              <ReactFlagsSelect
                id="currency-country-select"
                selected={selectedCountry}
                onSelect={setSelectedCountryOverride}
                countries={countries.map(country => country.isoCode)}
                customLabels={countryLabels}
                placeholder="Select country…"
                searchable
                searchPlaceholder="Search…"
                className="w-full currency-flag-select"
                selectButtonClassName="!min-h-[3rem] !rounded-xl !border-gray-300 !bg-white !py-3 !px-4 text-left !text-base font-medium text-gray-900 shadow-sm hover:!bg-gray-50/80 focus-visible:!ring-2 focus-visible:!ring-green-600 focus-visible:!ring-offset-2"
                optionsSize={18}
                selectedSize={18}
                showOptionLabel={true}
                showSecondaryOptionLabel={true}
                showSelectedLabel={true}
                showSecondarySelectedLabel={true}
              />
            </div>

            <Features selectedCountry={selectedCountry} />

            <div className="border-t border-gray-100 pt-4">
              <Button
                onClick={updateCountryAndCurrency}
                disabled={isPending || isCartLoading}
                color="green"
                size="lg"
                className="w-full sm:w-auto min-w-[10rem]"
              >
                {isPending ? (
                  <>
                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5" />
                    Saving…
                  </>
                ) : (
                  <>Set your location</>
                )}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

interface CountryClientProps {
  countries: {
    id: number
    name: string
    isoCode: string
    brandCode: string
    currencyCode: string
  }[]
}
